import { Button, IconButton, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import OrbitAIAddonModal from 'src/content/orbitAi/Components/OrbitAIAddonModal';
import { priceOrbitText } from 'src/content/dashboard/TextMessages';
import { getAddon } from 'src/common/Scraper';
import { isLocalSeoUser, localStorageMethod } from 'src/common/config/FunctionHelper';

interface OrbitAiBannerPlanPageProps {
  selectedValue?: string;
  isFromInitialSetup?: boolean;
  isLocalSeo?: boolean;
}

export const OrbitAiBannerPlanPage = ({
  selectedValue = 'annually',
  isFromInitialSetup = false,
  isLocalSeo: isLocalSeoProp
}: OrbitAiBannerPlanPageProps) => {
  const [openAddOnModal, setOpenAddOnModal] = useState(false);
  const [addonPlans, setAddonPlans] = useState<any[]>([]);

  const isAlreadyPurchased = useSelector(
    (state: RootState) =>
      Boolean((state.counter?.userDetails as any)?.isOrbitAiAddonPurchased) ||
      Boolean((state.counter?.userDetails as any)?.is_orbit_ai_addon_purchased) ||
      Boolean((state as any).dashboard?.isAiAddonPurchased)
  );

  const isLocalSeoUserRedux = useSelector(
    (state: RootState) =>
      Boolean((state.counter?.userDetails as any)?.isLocalSeo) ||
      Boolean((state as any).user?.isLocalSeoPlan) ||
      (state as any).user?.activePlan === 'localseo'
  );
  const isLocalSeo =
    isLocalSeoProp ?? (isLocalSeoUserRedux || Boolean(isLocalSeoUser()));

  const [isAddedInInitialSetup, setIsAddedInInitialSetup] = useState(() => {
    if (!isFromInitialSetup) return false;
    return localStorageMethod('get', 'addOrbitAiAddon') === 'true';
  });

  useEffect(() => {
    fetchAddonPlans();
  }, []);

  const fetchAddonPlans = async () => {
    try {
      const type = 'orbitaiaddon';
      const response: any = await getAddon(true, type);
      if (response?.status === 'success') {
        let plans = response?.plans || [];
        if (
          plans.some(
            (p: any) =>
              p?.metadata?.type === type ||
              p?.metadata?.type === 'orbitaiaddon' ||
              p?.metadata?.type === 'orbit_ai_addon'
          )
        ) {
          plans = plans.filter(
            (plan: any) =>
              plan?.metadata?.type === type ||
              plan?.metadata?.type === 'orbitaiaddon' ||
              plan?.metadata?.type === 'orbit_ai_addon'
          );
        }
        setAddonPlans(plans);
      }
    } catch (err) {
      console.error('Failed to fetch Orbit AI add-on plans:', err);
    }
  };

  const isAnnually = selectedValue === 'annually';

  const yearlyPlan = addonPlans.find(
    (p: any) =>
      p?.recurring?.interval === 'year' ||
      p?.metadata?.interval === 'yearly' ||
      p?.metadata?.interval === 'year'
  );
  const monthlyPlan = addonPlans.find(
    (p: any) =>
      p?.recurring?.interval === 'month' ||
      p?.metadata?.interval === 'monthly' ||
      p?.metadata?.interval === 'month'
  );

  const currentPlan = isAnnually ? yearlyPlan : monthlyPlan;

  const priceAmount = currentPlan?.unit_amount
    ? (currentPlan.unit_amount / 100).toFixed(2)
    : isAnnually
    ? '952.00'
    : '119.00';

  const handleToggleInitialSetup = () => {
    const newAdded = !isAddedInInitialSetup;
    setIsAddedInInitialSetup(newAdded);
    localStorageMethod('set', 'addOrbitAiAddon', newAdded ? 'true' : 'false');
    if (currentPlan?.id) {
      localStorageMethod('set', 'orbitAiAddonPriceId', newAdded ? currentPlan.id : '');
    }
  };

  useEffect(() => {
    if (isFromInitialSetup && isAddedInInitialSetup && currentPlan?.id) {
      localStorageMethod('set', 'orbitAiAddonPriceId', currentPlan.id);
    }
  }, [currentPlan?.id, isAnnually, isFromInitialSetup, isAddedInInitialSetup]);

  const bannerItems = [
    {
      dotColor: 'bg-secondary',
      text: '4 review replies drafted and sent',
      tag: '· Review Automation',
      status: 'Done',
      statusBg: 'bg-[#DDFDF6]',
      statusColor: 'text-[#0D7A63]'
    },
    {
      dotColor: 'bg-[#FEBB1A]',
      text: '2 pages competing for "linen bedding"',
      tag: '· Keyword Assist',
      status: 'Needs you',
      statusBg: 'bg-[#FFF5D9]',
      statusColor: 'text-[#8A6400]'
    },
    {
      dotColor: 'bg-[#DD588D]',
      text: '3 topics added to your content plan',
      tag: '· Content Strategy',
      status: 'New',
      statusBg: 'bg-[#F5F0FE]',
      statusColor: 'text-[#130243]'
    }
  ];

  const agentTags = [
    'Keyword Assist',
    'Blog Gen',
    'Local SEO',
    'Review Automation',
    'Content Strategy'
  ];
  const isMobile = useMediaQuery('(max-width:619px)');

  return (
    <>
      <div
        className="flex mx-auto max-w-[1600px]"
        style={{
          paddingLeft: isMobile ? '20px' : '80px',
          paddingRight: isMobile ? '20px' : '80px'
        }}
      >
        <div className="my-1 rounded-lg shadow-[0px_1px_8px_0px_var(--color-shadow-card)] bg-white overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 flex gap-2 border-b border-b-[#E0E0E6]">
            <p className="text-xl font-bold text-primary">Orbit AI Agents</p>
            <img
              src="/static/images/icons/ai_visibility.svg"
              alt="ai-visibility-icon"
            />
          </div>
          <div className="p-6 pt-0 bg-[#ECFDF3]">
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5 lg:gap-6">
              {/* Left */}
              <div className="bg-themeWhite max-h-36 rounded-b-2xl shadow-[0_6px_12px_2px_rgba(0,0,0,0.08)] px-4 py-2 lg:w-[350px] w-full">
                {bannerItems.map((item, index) => (
                  <div
                    key={index}
                    className="py-2 flex items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-2 w-2 rounded-full ${item.dotColor} shrink-0`}
                      />
                      <p className="text-xs font-normal text-[#373F41]">
                        {item.text}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] font-semibold px-2 py-1 rounded-full ${item.statusBg} ${item.statusColor} shrink-0`}
                    >
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>

              {/* Right */}
              <div className="pt-0 lg:pt-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                  <div className="flex flex-col gap-2">
                    <p className="text-base font-semibold">
                      {priceOrbitText?.modalTitle}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {agentTags?.map((agentTag, index) => (
                        <div
                          key={index}
                          className="py-1 px-2 rounded-full border border-[#AA95E2] bg-white"
                        >
                          <p className="text-sm font-medium text-primary">
                            {agentTag}
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-primary">
                      {priceOrbitText?.modalDesc}
                    </p>
                  </div>
                  <div className="flex flex-col gap-4 md:gap-2 w-full md:w-auto">
                    <div className="flex items-end justify-center gap-1">
                      <span className="text-3xl lg:text-[32px] xl:text-[40px] font-bold text-themeBlue leading-none">
                        {`$${priceAmount}`}
                      </span>
                      <span className="text-xl font-bold leading-none text-themeBlue">
                        {isAnnually ? '/yr' : '/mo'}
                      </span>
                    </div>
                    {isFromInitialSetup ? (
                      isAddedInInitialSetup ? (
                        <div className="w-full md:w-[275px] flex items-center gap-2">
                          <Button
                            variant="contained"
                            disabled={isAddedInInitialSetup || isLocalSeo}
                            className="flex-1 !rounded-full !text-base px-4 py-2 !font-semibold !bg-[#0D7A63] !text-white flex items-center justify-center gap-1.5"
                            // onClick={handleToggleInitialSetup}
                          >
                            <CheckIcon className="!text-xl" /> Added
                          </Button>
                          <IconButton
                            onClick={handleToggleInitialSetup}
                            disabled={isLocalSeo}
                            title="Remove Add-On"
                            className="!bg-[#FEE2E2] hover:!bg-[#FCA5A5] !text-[#DC2626] !p-2 !rounded-full shrink-0"
                          >
                            <CloseIcon className="!text-xl" />
                          </IconButton>
                        </div>
                      ) : (
                        <Button
                          variant="contained"
                          disabled={isLocalSeo}
                          className="w-full md:w-[275px] !rounded-full !text-base px-8 py-2 !font-semibold"
                          onClick={handleToggleInitialSetup}
                        >
                          Add To Subscription
                        </Button>
                      )
                    ) : (
                      <Button
                        variant="contained"
                        disabled={isAlreadyPurchased || isLocalSeo}
                        className="w-full md:w-[275px] !rounded-full !text-base px-8 py-2 !font-semibold"
                        onClick={() => !isAlreadyPurchased && !isLocalSeo && setOpenAddOnModal(true)}
                      >
                        {isAlreadyPurchased ? 'Already Subscribed' : 'Add To Subscription'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <OrbitAIAddonModal
        open={openAddOnModal}
        onClose={() => setOpenAddOnModal(false)}
      />
    </>
  );
};
